/**
 * Types of the module. Shared by server and web, and the contract of the HTTP API. What other
 * modules may use is exported from index.ts and listed in the README under "Öffentliche API".
 */

/** The 16 states by the abbreviation `projects.object.state` uses, e.g. `BE` for Berlin. */
export const STATES = [
  'BW',
  'BY',
  'BE',
  'BB',
  'HB',
  'HH',
  'HE',
  'MV',
  'NI',
  'NW',
  'RP',
  'SL',
  'SN',
  'ST',
  'SH',
  'TH',
] as const;
export type State = (typeof STATES)[number];

/**
 * Where a source applies: federal law, the model codes of the building ministers' conference and
 * DIBt (MBO, MVStättV …) or one state.
 */
export const JURISDICTIONS = ['federal', 'model', ...STATES] as const;
export type Jurisdiction = (typeof JURISDICTIONS)[number];

/** `standard` (DIN, VDE …) is a reference only: its wording is protected and not stored. */
export const SOURCE_KINDS = [
  'law',
  'regulation',
  'administrative-provision',
  'guideline',
  'technical-rule',
  'dguv',
  'standard',
] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

export const LEGAL_TOPICS = [
  'fire-safety',
  'building-code',
  'site-safety',
  'occupational-safety',
  'building-services',
] as const;
export type LegalTopic = (typeof LEGAL_TOPICS)[number];

/**
 * Topics a specialty searches, e.g. for the AI draft of a finding. Occupational safety belongs to
 * the construction site; the workplace rules on escape routes carry the fire safety topic too.
 * The safety specialist (SiFa) inspects workplaces, where escape routes and fire extinguishers
 * count as well.
 */
export const SPECIALTY_TOPICS = {
  'fire-safety': ['fire-safety', 'building-code', 'building-services'],
  'site-safety': ['site-safety', 'occupational-safety'],
  'occupational-safety': ['occupational-safety', 'fire-safety'],
} as const satisfies Record<string, readonly LegalTopic[]>;
export type LegalSpecialty = keyof typeof SPECIALTY_TOPICS;

/**
 * Jurisdictions for a building in a state: federal law, model codes and the state. Without a
 * known state, all of them.
 */
export function jurisdictionsFor(state: string | null | undefined): Jurisdiction[] {
  const code = STATES.find((s) => s === state?.trim().toUpperCase());
  return code ? ['federal', 'model', code] : [...JURISDICTIONS];
}

/** A source as the API returns it. `GET /api/legal/sources`. */
export interface LegalSource {
  id: string;
  /** Citation code, e.g. `BauO Bln` or `ASR A2.3`. */
  code: string | null;
  title: string;
  jurisdiction: Jurisdiction;
  kind: SourceKind;
  topics: LegalTopic[];
  summary: string | null;
  /** Edition in words, e.g. "zuletzt geändert durch Gesetz vom 09.07.2026". */
  edition: string | null;
  /** Official publication; the norm view links there. */
  officialUrl: string | null;
  active: boolean;
  /** Norms with wording, 0 while none are stored or for a standard. */
  normCount: number;
  /** HTTPS address on an official portal the wording is downloaded from. */
  fetchUrl: string | null;
  /** Entry of the module's catalog of official sources the source came from. */
  catalogKey: string | null;
  /** The catalog knows the source but no reader for its portal: no download. */
  parserMissing: boolean;
  /**
   * The portal (juris) offers its XML export only in the browser: a person downloads it there and
   * uploads it on the page of the source.
   */
  uploadOnly: boolean;
  /** Last successful download. */
  fetchedAt: string | null;
  updatedAt: string;
  version: number;
}

/** One line of the table of contents of a source. `GET /api/legal/sources/:id/contents`. */
export interface LegalContentsEntry {
  id: string;
  /** `§ 35`, `5.3`, `Anhang 2`, or `Eingang` for title and preamble. */
  number: string;
  heading: string | null;
  /** Part and section the norm belongs to, e.g. "Vierter Teil – Bauliche Anlagen". */
  outline: string | null;
  /** Length of the wording in characters. */
  chars: number;
  /** Start of the wording of a norm without heading ("3.1 Erste Hilfe umfasst …"), else `null`. */
  preview: string | null;
}

/** A norm found by the full-text search. `GET /api/legal/norms/search`. */
export interface LegalSearchHit {
  id: string;
  sourceId: string;
  code: string | null;
  sourceTitle: string;
  jurisdiction: Jurisdiction;
  number: string;
  heading: string | null;
  /** Passage around the matches, plain text. */
  excerpt: string;
  /** Matched words in `excerpt` as `[start, end)` character ranges. */
  marks: [number, number][];
  /** The query named this norm as a citation, e.g. "§ 35 BauO Bln". */
  cited: boolean;
}

/** A norm with its wording. `GET /api/legal/norms/:id`. */
export interface LegalNorm {
  id: string;
  sourceId: string;
  code: string | null;
  sourceTitle: string;
  jurisdiction: Jurisdiction;
  edition: string | null;
  officialUrl: string | null;
  number: string;
  heading: string | null;
  outline: string | null;
  text: string;
  /** The paragraph `text` was cut to, e.g. 2 for "(2) …"; `null` for the whole norm. */
  paragraph: number | null;
  /** `text` was shortened to the requested length. */
  truncated: boolean;
  /** Neighbours in the source, for paging. */
  previousId: string | null;
  nextId: string | null;
}

/** A citation of a legal basis and the norm it names. `GET /api/legal/norms/resolve`. */
export interface LegalReference {
  /** The citation as written, e.g. "§ 35 Abs. 5 MBO". */
  citation: string;
  /** `null` when no stored norm matches or the citation names a standard. */
  normId: string | null;
  paragraph: number | null;
}

/** A norm the AI named for a finding, checked against the stored wording. */
export interface LegalDraftReference {
  normId: string;
  sourceId: string;
  code: string | null;
  sourceTitle: string;
  jurisdiction: Jurisdiction;
  number: string;
  heading: string | null;
  paragraph: number | null;
  /** Literal quote of at most 200 characters; shorter than 12 characters it is dropped. */
  quote: string | null;
  /**
   * The quote appears in the wording word for word (apart from case, quotation marks, dashes and
   * spaces; "…" may skip text), or there is no quote. A verified quote decides the paragraph.
   */
  verified: boolean;
  officialUrl: string | null;
  /** E.g. "BauO Bln § 35 Abs. 2", the form a legal basis is written in. */
  citation: string;
}

/** Draft of a finding from bullet points (`LegalAssist.draftFinding`). Saved only by the person. */
export interface LegalFindingDraft {
  /**
   * `false` when the bullet points describe no defect, or with `positive` nothing positive;
   * `noFindingReason` says why.
   */
  isFinding: boolean;
  noFindingReason: string | null;
  title: string;
  description: string;
  measure: string;
  /** Active entries of the module's categories and of the kernel's priorities, or `null`. */
  categoryId: string | null;
  priorityId: string | null;
  /**
   * Entry of the module's defect catalog the model chose; title, texts, category, priority and
   * legal basis it leaves open come from there.
   */
  templateId: string | null;
  references: LegalDraftReference[];
  /**
   * For the legal basis of the finding: that of the template, then the citations of verified
   * references, joined with "; ". Unverified references are left out.
   */
  legalBasis: string;
}

/**
 * How a document from an official source came: XML of gesetze-im-internet.de or a state portal,
 * HTML, PDF, or an uploaded text file with the full text.
 */
export const RETRIEVAL_FORMATS = ['gii-xml', 'html', 'pdf', 'text'] as const;
export type RetrievalFormat = (typeof RETRIEVAL_FORMATS)[number];

/** States of a retrieval; `queued`, `running` and `ready` are open, one per source at most. */
export const RETRIEVAL_STATUSES = [
  'queued',
  'running',
  'ready',
  'unchanged',
  'accepted',
  'discarded',
  'failed',
] as const;
export type RetrievalStatus = (typeof RETRIEVAL_STATUSES)[number];

/** Latest download of a source. `GET /api/legal/retrievals` (one per source). */
export interface LegalRetrieval {
  id: string;
  sourceId: string;
  /** Download address; `null` for an uploaded file. */
  url: string | null;
  /** Downloaded from the portal or read from a file a person uploaded. */
  origin: 'download' | 'upload';
  status: RetrievalStatus;
  /** Reason of `failed`, e.g. `host-not-allowed`, `status`, `no-text`. */
  error: string | null;
  format: RetrievalFormat | null;
  /** Edition as the document states it. */
  edition: string | null;
  /** New, changed and removed norms by number; `null` before the download. */
  changes: { created: string[]; updated: string[]; removed: string[]; unchanged: number } | null;
  /** Norms whose wording was not found in the document; never taken over. */
  unverified: string[];
  /** Share of the document in verified norms, in percent. */
  coverage: number | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** A changed norm before and after, shortened. `GET /api/legal/retrievals/:id`. */
export interface LegalRetrievalChange {
  number: string;
  heading: string | null;
  before: string | null;
  after: string | null;
}

export interface LegalRetrievalDetail extends LegalRetrieval {
  /** Up to 20 new or changed norms with the beginning of their wording. */
  samples: LegalRetrievalChange[];
}

/** An entry of the catalog of official sources in the code. `GET /api/legal/catalog`. */
export interface LegalCatalogEntry {
  key: string;
  code: string;
  title: string;
  jurisdiction: Jurisdiction;
  kind: SourceKind;
  topics: LegalTopic[];
  /** Download address; `null` for a reference without wording (DGUV rules and information). */
  url: string | null;
  officialUrl: string;
  /** Why there is no reader for the portal; `null` when the source can be downloaded. */
  parserMissing: string | null;
  /** Only by uploading the XML export of the portal (juris). */
  uploadOnly: boolean;
  /** The source of the company made from the entry. */
  sourceId: string | null;
}
